/** ETFs — the third universe a model can be built from. */
import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { GET_ALL_ETFS } from '../queries';
import { EMPTY } from './_shared';

/**
 * The ETF master, a page at a time.
 *
 * Paged rather than fetched whole because the composer only ever shows a
 * page of candidates, and `search` narrows what has been fetched rather than
 * asking the server — the resolver takes a category but no query string, so a
 * text filter here is honest about only covering the rows on screen.
 */
export function useEtfUniverse({ search = '', category = null, page = 0, perPage = 25, skip = false } = {}) {
  const { data, loading, error } = useQuery(GET_ALL_ETFS, {
    /* One extra row is asked for, which is how "is there another page" is
       answered without a count the resolver does not return. */
    variables: { skip: page * perPage, limit: perPage + 1, category: category || null },
    skip,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  return useMemo(() => {
    const all = data?.getAllEtfs ?? EMPTY;
    const rows = all.slice(0, perPage);
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const matches = words.length
      ? rows.filter((e) => {
        const hay = `${e.schemeName || ''} ${e.securitySymbol || ''} ${e.category || ''}`.toLowerCase();
        return words.every((w) => hay.includes(w));
      })
      : rows;

    return {
      loading,
      error,
      rows: matches,
      /** Whether the page after this one has anything on it. */
      hasMore: all.length > perPage,
      /** Narrowed on this page only — say so where it is shown. */
      filtered: words.length > 0,
    };
  }, [data, loading, error, search, perPage]);
}
